import { Box, Button, Tab, Tabs } from "@mui/material";
import { getProducts } from "api/thunk/products/getActions/getProducts";
import { ReactNode, SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "shared/config/configureStore";
import ProductsTab from "./Tabs/ProductsTab/ProductsTab";
import ProductsCategoriesTab from "./Tabs/ProductsCategoriesTab/ProductsCategoriesTab";
import { getProductsCategories } from "api/thunk/productCategories/getActions/getProductsCategories";
import { useAppSelector, useDebounce } from "shared/lib/hooks/hooks";
import { setProductsCategoriesPage } from "api/slice/productCategoriesSlice/productCategoriesSlice";
import { setProductsPage } from "api/slice/productsSlice/productsSlice";

interface TabItem {
  label: string;
  component: ReactNode;
}

const Marketplace = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const marathonsLookupId = useAppSelector((state) => state.marathons.marathonsLookupId);
  const productCategoryId = useAppSelector((state) => state.productsCategories.productCategoryId);
  const { page: productCategoriesPage, pageSize: productCategoriesPageSize } = useAppSelector(
    (state) => state.productsCategories.productsCategories.params
  );
  const { page: productsPage, pageSize: productsPageSize } = useAppSelector(
    (state) => state.products.products.params
  );

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const [searchCategories, setSearchCategories] = useState("");
  const debouncedSearchCategories = useDebounce(searchCategories, 300);

  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState(
    tabFromUrl === "products" ? 1 : 0
  );

  const handleChangeTab = (_: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    setSearchParams({
      tab: newValue === 1 ? "products" : "categories",
    });
  };

  const tabs: TabItem[] = [
    {
      label: t("marketplace.productsCategories"),
      component:
        <ProductsCategoriesTab
          searchCategories={searchCategories}
          setSearchCategories={setSearchCategories}
        />
    },
    {
      label: t("marketplace.products"),
      component: <ProductsTab
        search={search}
        setSearch={setSearch}
      />
    },
  ];


  useEffect(() => {
    dispatch(setProductsPage(1));
  }, [dispatch, debouncedSearch]);

  useEffect(() => {
    dispatch(getProducts({
      page: productsPage,
      pageSize: productsPageSize,
      searchTerm: debouncedSearch,
      marathonId: marathonsLookupId ?? marathonsLookupId,
      productCategoryId: productCategoryId ?? productCategoryId
    }));
  }, [dispatch, productsPage, productsPageSize, debouncedSearch, marathonsLookupId, productCategoryId]);

  useEffect(() => {
    dispatch(setProductsCategoriesPage(1));
  }, [dispatch, debouncedSearchCategories]);

  useEffect(() => {
    dispatch(getProductsCategories({
      page: productCategoriesPage,
      pageSize: productCategoriesPageSize,
      searchTerm: debouncedSearchCategories,
    }));
  }, [dispatch, productCategoriesPage, productCategoriesPageSize, debouncedSearchCategories]);

  return (
    <Box
      sx={{
        padding: "30px 0",
        margin: '0 auto',
        maxWidth: '1240px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          sx={{
            color: '#0090D1',
            '& .MuiTabs-indicator': {
              backgroundColor: '#0090D1',
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              sx={{
                textTransform: 'none',
                fontSize: "20px",
                color: '#3E444A',
                '&.Mui-selected': {
                  color: '#0090D1',
                  fontWeight: 600,
                },
              }}
            />
          ))}
        </Tabs>

        <Box sx={{ display: "flex", gap: "12px" }}>
          {activeTab === 0 && (
            <Button
              variant="contained"
              sx={{
                borderRadius: '12px',
                textTransform: "none",
                background: '#0090D1',
                color: '#FFFFFF'
              }}
              onClick={() => navigate("/admin_panel/create-category")}
            >
              {t("marketplace.createCategory")}
            </Button>
          )}
          {activeTab === 1 && (
            <Button
              variant="contained"
              sx={{
                borderRadius: '12px',
                textTransform: "none",
                background: '#0090D1',
                color: '#FFFFFF'
              }}
              onClick={() => navigate("/admin_panel/create-product")}
            >
              {t("marketplace.createProduct")}
            </Button>
          )}
        </Box>
      </Box>

      <Box>
        {tabs[activeTab].component}
      </Box>
    </Box>

  );
};

export default Marketplace;